import { useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";

import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { Modal } from "antd";
import { z } from "zod";

import { Button } from "@/components/atoms/button/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/utils/form";
import { Input } from "@/components/utils/input";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { fullNameRegex } from "@/utils/regex";
import type { Push } from "@/utils/types";

type Step = "nameAndEmail" | "error";

export const nameAndEmailSchema = z.object({
  fullName: z
    .string({ required_error: "Este campo é obrigatório" })
    .min(
      3,
      "Não utilize abreviações. Este campo deve conter pelo menos duas palavras (Ex.: Nome Sobrenome).",
    )
    .trim()
    .max(120, "Nome muito longo.")
    .regex(
      fullNameRegex,
      "Não utilize abreviações. Este campo deve conter pelo menos duas palavras (Ex.: Nome Sobrenome).",
    ),
  email: z
    .string({ required_error: "Este campo é obrigatório" })
    .max(
      320,
      "E-mail inválido, por favor insira um e-mail conforme o exemplo a seguir: email@corporativo.com.br",
    )
    .trim()
    .email(
      "E-mail inválido, por favor insira um e-mail conforme o exemplo a seguir: email@corporativo.com.br",
    )
    .refine(
      (value) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/;
        return emailRegex.test(value);
      },
      {
        message:
          "E-mail inválido, por favor insira um e-mail conforme o exemplo a seguir: email@corporativo.com.br",
      },
    ),
});

export type FormData = z.infer<typeof nameAndEmailSchema>;

type Props = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  candidateKey: number;
  jobTitle?: string;
};

export default function NameAndEmailModal({
  open,
  setOpen,
  candidateKey,
  jobTitle,
}: Props) {
  const [step, setStep] = useState<Step>("nameAndEmail");
  const [storageData, setStorageData] = useLocalStorage<Push>(
    "push",
    {} as Push,
  );
  const [value, copy] = useCopyToClipboard(3000);

  const router = useRouter();

  const form = useForm<FormData>({
    defaultValues: {
      fullName: storageData?.fullName ?? "",
      email: storageData?.email ?? "",
    },
    resolver: zodResolver(nameAndEmailSchema),
  });

  const onCancel = () => {
    setOpen(false);
    setStep("nameAndEmail");
  };

  const onSubmit: SubmitHandler<FormData> = (data) => {
    if (nameAndEmailSchema.safeParse(data).success) {
      setStorageData({ ...storageData, ...data });
      setOpen(false);
      if (process.env.NODE_ENV === "production") {
        // postHireNewTalent({
        //   fullName: data.fullName,
        //   email: data.email,
        // })
        //   .then(() =>
        //     router.push(
        //       window.location.pathname +
        //         `/perfis?tab=Perfil+${candidateKey || 1}`,
        //     ),
        //   )
        //   .catch((error) => console.error(error));
      } else
        router.push(
          window.location.pathname + `/perfis?tab=Perfil+${candidateKey || 1}`,
        );
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={600}
      centered={typeof window !== "undefined" && window.innerWidth < 600}
      title={
        <div
          className="flex cursor-pointer flex-row gap-4"
          onClick={() => onCancel()}
        >
          <ArrowLeftOutlined className="text-base text-aubergine-perl-600" />
          <p className="font-normal">Voltar</p>
        </div>
      }
      closeIcon={
        <CloseCircleOutlined className="text-2xl text-aubergine-perl-600" />
      }
    >
      {step === "nameAndEmail" && (
        <Form {...form}>
          <h2 className="mb-4 mt-2 text-2xl font-medium">
            Abrir a análise gratuita
          </h2>
          <p className="mb-4 text-base font-normal sm:text-xl">
            Preencha seu nome e e-mail nos campos abaixo e acesse{" "}
            <span className="font-medium">imediatamente</span> os perfis
            selecionados para você.
          </p>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
          >
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="fullName">
                    <span className="text-aubergine-perl-600">*</span> Seu nome
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      id="fullName"
                      className="w-full"
                      placeholder="Nome completo"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="email">
                    <span className="text-aubergine-perl-600">*</span> E-mail
                    corporativo
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      id="email"
                      className="w-full"
                      placeholder="email@corporativo.com.br"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              data-gtm-event-category={`push:${jobTitle}`}
              data-gtm-event-action="clique:link"
              data-gtm-event-label="acessar_analise_perfil"
              type="submit"
              className="w-full"
              color="primary"
            >
              Acessar análise do perfil
            </Button>
          </form>
        </Form>
      )}
      {step === "error" && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-center">
            <Image
              src="/imgs/server-error-illustration.svg"
              width={252}
              height={231}
              unoptimized
              alt="Algo deu errado"
              aria-hidden
            />
          </div>
          <div className="flex items-center justify-center">
            <span className="font-poppins text-2xl">Algo deu errado</span>
          </div>
          <div className="flex flex-row items-center justify-center">
            <p className="text-center text-base">
              Tente novamente ou envie um e-mail para o endereço a seguir:{" "}
              <Link
                href="mailto: atendimento@harpio.com.br"
                className="text-center text-base text-brand-primary underline"
              >
                atendimento@harpio.com.br
              </Link>
              {value ? (
                <span className="ml-2 text-brand-primary">
                  <CheckCircleOutlined /> Copiado
                </span>
              ) : (
                <CopyOutlined
                  className="ml-1 cursor-pointer text-brand-primary"
                  onClick={() => void copy("atendimento@harpio.com.br")}
                />
              )}
            </p>
          </div>
          <div className="flex items-center justify-center">
            <Button
              className="cursor-pointer sm:px-12"
              type="button"
              color="primary"
              rounded="full"
              onClick={() => {
                form.reset({});
                setStep("nameAndEmail");
              }}
            >
              {form.formState.isSubmitting ? (
                <LoadingOutlined className="size-4" />
              ) : (
                "Tentar novamente"
              )}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
